import { useState, useMemo } from 'react';
import styles from './OSDrawer.module.css';

// Bloco 5.1 - clientes e produtos vem via props (carregados pelo OS.jsx)
// Materiais previstos sao gravados dentro do doc da OS (array).

export default function OSDrawer({
  os,
  clientes = [],
  produtos = [],
  onSalvar,
  onFechar,
  salvando = false,
  somenteLeitura = false,
}) {
  const editando = !!os;
  const ro = somenteLeitura;

  function dadosProduto(produtoId) {
    return produtos.find((p) => p.id === produtoId);
  }

  const [abaAtiva, setAbaAtiva] = useState('dados');

  const [numero, setNumero] = useState(os?.numero || '');
  const [clienteId, setClienteId] = useState(os?.clienteId || '');
  const [contato, setContato] = useState(os?.contato || '');
  const [area, setArea] = useState(os?.area || '');
  const [descricao, setDescricao] = useState(os?.descricao || '');
  const [status, setStatus] = useState(os?.status || 'Aberta');
  const [dataInicio, setDataInicio] = useState(os?.dataInicio || '');
  const [dataTermino, setDataTermino] = useState(os?.dataTermino || '');

  // Materiais previstos guardam só o que o usuário planejou.
  // O "realizado" mostrado vem das movimentações (calculado em OS.jsx),
  // mas dentro do drawer mostramos o que veio em os.materiais.
  const [materiais, setMateriais] = useState(
    (os?.materiais || []).map((m) => ({
      produtoId: m.produtoId,
      previsto: m.previsto ?? '',
      realizado: m.realizado || 0,
      naoPrevisto: m.naoPrevisto || false,
    }))
  );

  const [pickerAberto, setPickerAberto] = useState(false);
  const [buscaProduto, setBuscaProduto] = useState('');

  const documentos = os?.documentos || [];

  const valido =
    numero.trim().length > 0 && clienteId.length > 0 && area.trim().length > 0;

  const totais = useMemo(() => {
    const comPrev = materiais.filter(
      (m) => m.previsto !== '' && m.previsto !== null && Number(m.previsto) > 0
    );
    const totalPrev = comPrev.reduce(
      (s, m) => s + (Number(m.previsto) || 0),
      0
    );
    const totalReal = materiais.reduce((s, m) => s + (m.realizado || 0), 0);
    const pct = totalPrev > 0 ? Math.round((totalReal / totalPrev) * 100) : null;
    return { totalPrev, totalReal, pct };
  }, [materiais]);

  const produtosFiltradosPicker = useMemo(() => {
    if (!buscaProduto) return produtos;
    const q = buscaProduto.toLowerCase();
    return produtos.filter(
      (p) =>
        (p.cor || '').toLowerCase().includes(q) ||
        (p.resina || '').toLowerCase().includes(q)
    );
  }, [buscaProduto, produtos]);

  function adicionarMaterial(produto) {
    if (materiais.some((m) => m.produtoId === produto.id)) return;
    setMateriais([
      ...materiais,
      { produtoId: produto.id, previsto: '', realizado: 0, naoPrevisto: false },
    ]);
    setPickerAberto(false);
  }

  function atualizarPrevisto(produtoId, valor) {
    setMateriais(
      materiais.map((m) =>
        m.produtoId === produtoId ? { ...m, previsto: valor } : m
      )
    );
  }

  function removerMaterial(produtoId) {
    setMateriais(materiais.filter((m) => m.produtoId !== produtoId));
  }

  function handleSalvar() {
    if (ro) return;
    if (!valido) {
      alert('Preencha: Número da OS, Cliente e Área.');
      return;
    }
    const clienteNome =
      clientes.find((c) => c.id === clienteId)?.nome || clienteId;
    // Só salva no doc da OS os MATERIAIS PREVISTOS (não os "não previstos",
    // que são calculados em runtime a partir das movimentações).
    const materiaisNorm = materiais
      .filter((m) => !m.naoPrevisto)
      .map((m) => ({
        produtoId: m.produtoId,
        previsto:
          m.previsto === '' || m.previsto === null
            ? null
            : parseInt(m.previsto, 10),
        naoPrevisto: false,
      }));
    onSalvar({
      numero: numero.trim(),
      clienteId,
      clienteNome,
      contato: contato.trim(),
      area: area.trim(),
      descricao: descricao.trim(),
      status,
      dataInicio,
      dataTermino,
      materiais: materiaisNorm,
      documentos,
    });
  }

  function pctMaterial(m) {
    if (m.naoPrevisto) return 100;
    const prev = Number(m.previsto) || 0;
    if (prev === 0) return 0;
    return Math.min(Math.round((m.realizado / prev) * 100), 100);
  }

  function classePct(m) {
    if (m.naoPrevisto) return styles.pctExcd;
    const pct = pctMaterial(m);
    if (pct >= 100) return styles.pctOk;
    return styles.pctParcial;
  }

  return (
    <div className={`${styles.drawerOverlay} ${styles.aberto}`}>
      <div className={styles.drawer}>
        <div className={styles.drawerHead}>
          <div className={styles.drawerHeadTop}>
            <div>
              <div className={styles.drawerTitle}>
                {editando ? `${os.numero} — ${os.clienteNome}` : 'Nova OS'}
                {ro && (
                  <span style={{
                    marginLeft: 10,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#666',
                    background: '#f0f0f0',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: '2px 7px',
                    verticalAlign: 'middle',
                  }}>
                    🔒 MODO LEITURA
                  </span>
                )}
              </div>
              <div className={styles.drawerSub}>
                {editando
                  ? `${os.contato ? os.contato + ' · ' : ''}${os.area}`
                  : 'Preencha os dados da ordem de serviço'}
              </div>
            </div>
            <button className={styles.drawerClose} onClick={onFechar}>
              ✕
            </button>
          </div>
          <div className={styles.drawerTabs}>
            <div
              className={`${styles.drawerTab} ${
                abaAtiva === 'dados' ? styles.active : ''
              }`}
              onClick={() => setAbaAtiva('dados')}
            >
              Dados
            </div>
            <div
              className={`${styles.drawerTab} ${
                abaAtiva === 'materiais' ? styles.active : ''
              }`}
              onClick={() => setAbaAtiva('materiais')}
            >
              Materiais
            </div>
            <div
              className={`${styles.drawerTab} ${
                abaAtiva === 'documentos' ? styles.active : ''
              }`}
              onClick={() => setAbaAtiva('documentos')}
            >
              Documentos
            </div>
          </div>
        </div>

        <div className={styles.drawerBody}>
          {abaAtiva === 'dados' && (
            <>
              <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>Identificação</div>
                <div className={styles.formGrid2}>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Número da OS <span className={styles.obrigatorio}>*</span>
                    </label>
                    <input
                      className={`${styles.formInput} ${styles.mono}`}
                      placeholder="Ex: OS-047"
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      readOnly={ro}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Status <span className={styles.obrigatorio}>*</span>
                    </label>
                    <select
                      className={styles.formSelect}
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={ro}
                    >
                      <option value="Aberta">Aberta</option>
                      <option value="Em execução">Em execução</option>
                      <option value="Concluída">Concluída</option>
                    </select>
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Cliente <span className={styles.obrigatorio}>*</span>
                    </label>
                    <select
                      className={styles.formSelect}
                      value={clienteId}
                      onChange={(e) => setClienteId(e.target.value)}
                      disabled={ro}
                    >
                      <option value="">Selecione...</option>
                      {clientes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>
                      Área <span className={styles.obrigatorio}>*</span>
                    </label>
                    <input
                      className={styles.formInput}
                      placeholder="Ex: Aciaria, Laminação"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      readOnly={ro}
                    />
                  </div>
                  <div className={`${styles.formField} ${styles.full}`}>
                    <label className={styles.formLabel}>
                      Contato (nesta OS)
                    </label>
                    <input
                      className={styles.formInput}
                      placeholder="Nome do contato (opcional)"
                      value={contato}
                      onChange={(e) => setContato(e.target.value)}
                      readOnly={ro}
                    />
                    <div className={styles.formHint}>
                      Pessoa de referência nesta obra específica. Opcional.
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>
                  Descrição do serviço
                </div>
                <div className={styles.formField}>
                  <textarea
                    className={styles.formTextarea}
                    style={{ height: 80 }}
                    placeholder="Ex: Jateamento Sa2½ e pintura de estruturas..."
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    readOnly={ro}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>Cronograma</div>
                <div className={styles.formGrid2}>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Início previsto</label>
                    <input
                      className={styles.formInput}
                      type="date"
                      value={dataInicio}
                      onChange={(e) => setDataInicio(e.target.value)}
                      disabled={ro}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Término previsto</label>
                    <input
                      className={styles.formInput}
                      type="date"
                      value={dataTermino}
                      onChange={(e) => setDataTermino(e.target.value)}
                      disabled={ro}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {abaAtiva === 'materiais' && (
            <>
              <div className={styles.formSectionTitle}>
                Previsto × Realizado por produto
              </div>

              {materiais.length === 0 ? (
                <div className={styles.matEmpty}>
                  <div className={styles.matEmptyIcon}>📦</div>
                  Nenhum material previsto.
                  <br />
                  Adicione produtos previstos ou abra a OS sem prever nada.
                </div>
              ) : (
                <div className={styles.matTable}>
                  <div className={styles.matTh}>
                    <div>Produto</div>
                    <div>Prev</div>
                    <div>Real</div>
                    <div>%</div>
                    <div>Progresso</div>
                    <div></div>
                  </div>
                  {materiais.map((m) => {
                    const p = dadosProduto(m.produtoId);
                    const pct = pctMaterial(m);
                    return (
                      <div key={m.produtoId} className={styles.matTr}>
                        <div>
                          <div className={styles.matNome}>
                            {p ? (p.cor || '—') : m.produtoId}
                            {m.naoPrevisto && (
                              <span className={styles.matNaoprev}>
                                não prev.
                              </span>
                            )}
                          </div>
                          <div className={styles.matCor}>
                            {p ? `${p.cor || ''}${p.resina ? ' · ' + p.resina : ''}` : ''}
                          </div>
                        </div>
                        <div className={styles.matPrev}>
                          {m.naoPrevisto ? (
                            '—'
                          ) : (
                            <input
                              className={styles.matInput}
                              type="number"
                              min="0"
                              value={m.previsto}
                              onChange={(e) =>
                                atualizarPrevisto(m.produtoId, e.target.value)
                              }
                              readOnly={ro}
                            />
                          )}
                        </div>
                        <div className={styles.matReal}>{m.realizado}</div>
                        <div className={`${styles.matPct} ${classePct(m)}`}>
                          {m.naoPrevisto ? '—' : `${pct}%`}
                        </div>
                        <div className={styles.matProg}>
                          <div
                            className={styles.matProgFill}
                            style={{
                              width: `${pct}%`,
                              background: m.naoPrevisto
                                ? '#A32D2D'
                                : pct >= 100
                                ? '#0F6E56'
                                : pct === 0
                                ? '#ddd'
                                : '#B97A00',
                            }}
                          />
                        </div>
                        {!m.naoPrevisto && !ro && (
                          <button
                            className={styles.btnRm}
                            onClick={() => removerMaterial(m.produtoId)}
                            title="Remover"
                          >
                            ✕
                          </button>
                        )}
                        {(m.naoPrevisto || ro) && <div></div>}
                      </div>
                    );
                  })}
                  <div className={`${styles.matTr} ${styles.total}`}>
                    <div>TOTAL</div>
                    <div className={styles.matPrev}>{totais.totalPrev}</div>
                    <div className={styles.matReal}>{totais.totalReal}</div>
                    <div className={`${styles.matPct} ${styles.pctOk}`}>
                      {totais.pct !== null ? `${totais.pct}%` : '—'}
                    </div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
              )}

              {!ro && (
                <button
                  className={styles.matAdd}
                  onClick={() => {
                    setBuscaProduto('');
                    setPickerAberto(true);
                  }}
                >
                  + Adicionar produto previsto
                </button>
              )}

              <div className={styles.matResumo}>
                💡 O <strong>realizado</strong> vem das Saídas registradas na
                Movimentação (devoluções subtraem). Itens fora da lista prevista
                aparecem marcados como <strong>"não prev."</strong>
              </div>
            </>
          )}

          {abaAtiva === 'documentos' && (
            <>
              {documentos.length === 0 ? (
                <div className={styles.matEmpty}>
                  <div className={styles.matEmptyIcon}>📄</div>
                  Nenhum documento anexado a esta OS.
                </div>
              ) : (
                documentos.map((doc, idx) => (
                  <div key={idx} className={styles.docItem}>
                    <div className={styles.docIcon}>PDF</div>
                    <div className={styles.docInfo}>
                      <div className={styles.docNome}>{doc.nome}</div>
                      <div className={styles.docMeta}>
                        {os?.numero || 'Nova OS'} · {doc.tamanho}
                      </div>
                    </div>
                    <button
                      className={styles.docBtn}
                      onClick={() => alert('Download: ' + doc.nome)}
                    >
                      ⬇ Baixar
                    </button>
                  </div>
                ))
              )}
              {!ro && (
                <div
                  className={styles.docAdd}
                  onClick={() =>
                    alert('Upload de documento será implementado no Bloco 6 (Storage).')
                  }
                >
                  + Adicionar documento · PDF, DWG, JPG, PNG · máx 20 MB
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.drawerFoot}>
          <button className={styles.btnSecondary} onClick={onFechar}>
            {ro ? 'Fechar' : 'Cancelar'}
          </button>
          {!ro && (
            <button
              className={styles.btnPrimary}
              onClick={handleSalvar}
              disabled={!valido || salvando}
            >
              {salvando
                ? 'Salvando...'
                : editando
                ? 'Salvar alterações'
                : 'Criar OS'}
            </button>
          )}
        </div>
      </div>

      {pickerAberto && (
        <div className={`${styles.pickerOverlay} ${styles.aberto}`}>
          <div className={styles.pickerModal}>
            <div className={styles.pickerTitle}>Adicionar produto previsto</div>
            <div className={styles.pickerSub}>
              Selecione o produto que você prevê usar nesta OS.
            </div>
            <input
              className={styles.pickerSearch}
              placeholder="🔍 Buscar produto, cor, resina..."
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(e.target.value)}
              autoFocus
            />
            <div className={styles.pickerList}>
              {produtosFiltradosPicker.length === 0 ? (
                <div className={styles.pickerEmpty}>
                  Nenhum produto encontrado.
                </div>
              ) : (
                produtosFiltradosPicker.map((p) => {
                  const ja = materiais.some((m) => m.produtoId === p.id);
                  return (
                    <div
                      key={p.id}
                      className={`${styles.pickerItem} ${ja ? styles.ja : ''}`}
                      onClick={() => !ja && adicionarMaterial(p)}
                    >
                      <div>
                        <div className={styles.pickerNome}>{p.cor || '—'}</div>
                        <div className={styles.pickerMeta}>
                          {p.cor || ''}
                          {p.resina ? ' · ' + p.resina : ''}
                        </div>
                      </div>
                      {ja && (
                        <span style={{ fontSize: 11, color: '#888' }}>
                          já adicionado
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className={styles.pickerFoot}>
              <button
                className={styles.btnSecondary}
                onClick={() => setPickerAberto(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}